import { Check, ChevronRight, Copy, Loader2, Sparkles } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import { useListMedicalCodesByEncounter } from "@elation/api-client/src/stitch-web";

import {
  MEDICAL_CODE_GROUPS,
  MedicalCodeEntity,
  type MedicalCodeGroup,
} from "@src/features/medical-codes";
import { ErrorMessage } from "@src/shared/components/general/ErrorMessage";
import { Button } from "@src/shared/components/ui/button";
import { CollapsiblePanel } from "@src/shared/components/v2/CollapsiblePanel";
import { cn } from "@src/shared/lib/utils";

export interface CompletedVisitMedicalCodeV2 {
  id: string;
  code: string;
  description?: string | null;
  group: MedicalCodeGroup;
  groupLabel: string;
  evidence: string[];
}

export interface CompletedVisitCodesPanelProps {
  encounterId?: number;
  compact?: boolean;
}

export interface CompletedVisitCodesPanelBodyProps {
  codes: CompletedVisitMedicalCodeV2[];
  isLoading?: boolean;
  isError?: boolean;
  compact?: boolean;
}

export interface CompletedVisitCodesCollapsiblePanelProps {
  encounterId?: number;
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  compact?: boolean;
}

function codesClipboardText(codes: CompletedVisitMedicalCodeV2[]): string {
  return codes
    .map(
      (code) => `${code.groupLabel}\n${code.code}  ${code.description ?? ""}`,
    )
    .join("\n\n");
}

function useCompletedVisitMedicalCodes({
  encounterId,
}: {
  encounterId?: number;
}) {
  const medicalCodesQuery = useListMedicalCodesByEncounter(encounterId ?? 0, {
    query: {
      enabled: encounterId != null,
      refetchInterval: (query) => {
        return MedicalCodeEntity.medicalCodesPollIntervalMs(
          query.state.data?.data,
        );
      },
    },
  });
  const codes = useMemo<CompletedVisitMedicalCodeV2[]>(() => {
    const data = medicalCodesQuery.data?.data;
    if (!data) return [];

    const groups = MedicalCodeEntity.groupRows(data);

    return MEDICAL_CODE_GROUPS.flatMap((group) =>
      groups[group].map((row) => ({
        id: `${group}-${row.code}`,
        code: MedicalCodeEntity.codeLabel(row) ?? row.code,
        description: row.description,
        group,
        groupLabel: MedicalCodeEntity.groupLabel(group),
        evidence: MedicalCodeEntity.evidenceDisplayLines(row),
      })),
    );
  }, [medicalCodesQuery.data?.data]);

  return {
    codes,
    isLoading: medicalCodesQuery.isLoading || medicalCodesQuery.isFetching,
    isError: medicalCodesQuery.isError,
  };
}

export function CompletedVisitCodesPanel({
  encounterId,
  compact = false,
}: CompletedVisitCodesPanelProps) {
  const { codes, isLoading, isError } = useCompletedVisitMedicalCodes({
    encounterId,
  });

  return (
    <CompletedVisitCodesFrame codes={codes}>
      <CompletedVisitCodesPanelBody
        codes={codes}
        isLoading={isLoading}
        isError={isError}
        compact={compact}
      />
    </CompletedVisitCodesFrame>
  );
}

export function CompletedVisitCodesCollapsiblePanel({
  encounterId,
  open,
  defaultOpen = false,
  onOpenChange,
  compact = false,
}: CompletedVisitCodesCollapsiblePanelProps) {
  const { codes, isLoading, isError } = useCompletedVisitMedicalCodes({
    encounterId,
  });

  return (
    <CollapsiblePanel
      label="Codes"
      count={String(codes.length)}
      open={open}
      defaultOpen={defaultOpen}
      onOpenChange={onOpenChange}
      className="h-full"
      contentClassName="min-h-0 overflow-auto"
    >
      <CompletedVisitCodesPanelBody
        codes={codes}
        isLoading={isLoading}
        isError={isError}
        compact={compact}
      />
    </CollapsiblePanel>
  );
}

function CompletedVisitCodesFrame({
  codes,
  children,
}: {
  codes: CompletedVisitMedicalCodeV2[];
  children: React.ReactNode;
}) {
  const [copied, setCopied] = useState(false);
  const copyCodes = async () => {
    await navigator.clipboard.writeText(codesClipboardText(codes));
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="overflow-hidden rounded-[10px] border border-border bg-background">
      <div className="flex min-h-10 items-center justify-between gap-2 border-border border-b bg-surface px-3 py-1.5">
        <div className="flex min-w-0 items-center gap-2">
          <Sparkles className="size-3.5 shrink-0 text-primary" aria-hidden />
          <span className="font-mono text-[10.5px] font-medium uppercase tracking-[0.08em] text-muted-foreground">
            Codes
          </span>
          <span className="rounded-full bg-muted px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground">
            {codes.length}
          </span>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="size-7 rounded-md text-muted-foreground"
          aria-label={copied ? "Copied codes" : "Copy codes"}
          disabled={codes.length === 0}
          onClick={copyCodes}
        >
          {copied ? (
            <Check className="size-3.5 text-success" aria-hidden />
          ) : (
            <Copy className="size-3.5" aria-hidden />
          )}
        </Button>
      </div>
      {children}
    </div>
  );
}

export function CompletedVisitCodesPanelBody({
  codes,
  isLoading = false,
  isError = false,
  compact = false,
}: CompletedVisitCodesPanelBodyProps) {
  const [openCodeId, setOpenCodeId] = useState<string | null>(
    codes[0]?.id ?? null,
  );
  const groupedCodes = useMemo(() => {
    const groups = new Map<MedicalCodeGroup, CompletedVisitMedicalCodeV2[]>();
    for (const code of codes) {
      const group = groups.get(code.group) ?? [];
      group.push(code);
      groups.set(code.group, group);
    }
    return Array.from(groups.entries());
  }, [codes]);

  useEffect(() => {
    if (openCodeId != null || codes.length === 0) return;
    setOpenCodeId(codes[0].id);
  }, [codes, openCodeId]);

  if (isError) {
    return (
      <ErrorMessage
        className="block px-4 py-3"
        error={true}
        fallback="Unable to load codes."
        sizing="xs"
      />
    );
  }

  if (isLoading && codes.length === 0) {
    return (
      <div className="flex items-center gap-2 px-4 py-3 text-[13px] text-muted-foreground">
        <Loader2 className="size-3.5 animate-spin" aria-hidden />
        Generating codes...
      </div>
    );
  }

  if (groupedCodes.length === 0) {
    return (
      <p className="px-4 py-3 text-[13px] text-muted-foreground">
        No codes detected yet.
      </p>
    );
  }

  return (
    <div className="divide-y divide-border">
      {groupedCodes.map(([group, groupCodes]) => (
        <section key={group}>
          <div className="flex items-center justify-between bg-surface px-3 py-2">
            <h3 className="font-mono text-[10px] font-medium uppercase tracking-[0.08em] text-muted-foreground">
              {MedicalCodeEntity.groupSectionTitle(group)}
            </h3>
            <span className="font-mono text-[10px] text-muted-foreground">
              {groupCodes.length}
            </span>
          </div>
          <div className="divide-y divide-border">
            {groupCodes.map((code) => {
              const isOpen = openCodeId === code.id;
              const evidence = code.evidence?.filter(Boolean) ?? [];

              return (
                <div key={code.id}>
                  <Button
                    type="button"
                    variant="ghost"
                    className={cn(
                      "flex h-auto w-full justify-start gap-3 rounded-none px-3 py-3 text-left hover:bg-muted/40",
                      compact && "gap-2 px-2.5 py-2",
                    )}
                    aria-expanded={isOpen}
                    onClick={() =>
                      setOpenCodeId((current) =>
                        current === code.id ? null : code.id,
                      )
                    }
                  >
                    <span
                      className={cn(
                        "w-16 shrink-0 font-mono text-[12.5px] font-semibold text-foreground",
                        compact && "w-14 text-[11.5px]",
                      )}
                    >
                      {code.code}
                    </span>
                    <span
                      className={cn(
                        "min-w-0 flex-1 text-[13px] font-normal leading-snug text-muted-foreground",
                        compact && "text-[12px]",
                      )}
                    >
                      {MedicalCodeEntity.descriptionLabel(code)}
                    </span>
                    <ChevronRight
                      className={cn(
                        "mt-0.5 size-3.5 shrink-0 text-muted-foreground transition-transform",
                        isOpen && "rotate-90",
                      )}
                      aria-hidden
                    />
                  </Button>

                  {isOpen ? (
                    <div className="px-3 pb-3">
                      <div className="rounded-md border border-primary/15 bg-primary/5 px-3 py-2 text-[12px] leading-relaxed">
                        <span className="mr-2 font-mono text-[10px] font-medium uppercase tracking-[0.08em] text-primary">
                          From visit
                        </span>
                        <span className="italic text-foreground">
                          {evidence.length > 0
                            ? `"${evidence[0]}"`
                            : "No evidence attached to this code yet."}
                        </span>
                      </div>
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
}
